import React, { useEffect, useState } from "react";
import { CreditCard, Plus, Ban, Loader2, Copy, X, CheckCircle2, AlertTriangle } from "lucide-react";
import { adminApi } from "../lib/api";

export default function AdminSubscriptions() {
  const [data, setData] = useState({ items: [], total: 0, active: 0, revenue_active_xaf: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showActiveOnly, setShowActiveOnly] = useState(true);
  const [creating, setCreating] = useState(false);
  const [createdToken, setCreatedToken] = useState(null);

  const load = () => {
    setLoading(true);
    adminApi.listSubscriptions({ active_only: showActiveOnly })
      .then(({ data }) => setData(data))
      .catch((e) => setError(e?.response?.data?.detail || "Erreur"))
      .finally(() => setLoading(false));
  };
  useEffect(load, [showActiveOnly]);

  const revoke = async (sub) => {
    if (!window.confirm(`Révoquer l'abonnement de ${sub.email} ?`)) return;
    try {
      await adminApi.revokeSub(sub.id);
      load();
    } catch (err) {
      alert(err?.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div data-testid="admin-subscriptions-page">
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-accent mb-2">/ abonnements</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold text-primary tracking-tight">Abonnements & revenus</h1>
          <p className="text-sm text-foreground/60 mt-2 max-w-2xl">
            Liste des abonnés actifs. Créez un abonnement manuel lorsque PawaPay est indisponible ou pour un paiement hors ligne.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="inline-flex items-center gap-2 bg-primary text-white px-4 py-2.5 rounded-sm text-sm font-medium hover:bg-primary-light transition-colors"
          data-testid="subs-manual-new-btn"
        >
          <Plus size={14} /> Activation manuelle
        </button>
      </div>

      {error && (
        <div className="mb-4 text-sm text-[#991B1B] bg-[#FEE2E2] border border-[#FECACA] px-3 py-2 rounded-sm">
          <AlertTriangle size={14} className="inline mr-1 -mt-0.5" />{error}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6" data-testid="subs-stats">
        <StatBox icon={CreditCard} label="Actifs" value={data.active} />
        <StatBox label="Total" value={data.total} />
        <StatBox label="Revenu (actifs)" value={`${(data.revenue_active_xaf || 0).toLocaleString("fr-FR")} XAF`} />
      </div>

      <div className="mb-4 flex items-center gap-3">
        <label className="inline-flex items-center gap-2 text-xs text-foreground/70">
          <input
            type="checkbox" checked={showActiveOnly}
            onChange={(e) => setShowActiveOnly(e.target.checked)}
            data-testid="subs-filter-active"
          />
          Actifs uniquement
        </label>
      </div>

      {loading ? (
        <div className="text-sm text-foreground/50">Chargement…</div>
      ) : (
        <div className="bg-white border border-primary/15 rounded-sm overflow-x-auto" data-testid="subs-table">
          <table className="w-full text-sm">
            <thead className="bg-primary/5 text-primary font-mono uppercase text-[10px] tracking-widest">
              <tr>
                <th className="text-left px-4 py-3">Email</th>
                <th className="text-left px-4 py-3">Plan</th>
                <th className="text-left px-4 py-3">Source</th>
                <th className="text-left px-4 py-3">Créé</th>
                <th className="text-left px-4 py-3">Expire</th>
                <th className="text-right px-4 py-3">Montant</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.items.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-foreground/50">Aucun abonnement.</td></tr>
              )}
              {data.items.map((s) => (
                <tr key={s.id} className="border-t border-primary/10 hover:bg-primary/5">
                  <td className="px-4 py-3 font-mono text-xs">{s.email || "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm ${s.plan === "pro" ? "bg-accent text-[#0D1B2A]" : "bg-primary text-white"}`}>
                      {s.plan} · {s.billing}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-foreground/70">
                    {s.source === "manual" ? "Manuel" : "PawaPay"}
                    {s.created_by && <div className="text-foreground/40 text-[10px]">{s.created_by}</div>}
                  </td>
                  <td className="px-4 py-3 text-xs text-foreground/70">{s.created_at ? new Date(s.created_at).toLocaleDateString() : "—"}</td>
                  <td className="px-4 py-3 text-xs text-foreground/70">{s.expires_at ? new Date(s.expires_at).toLocaleDateString() : "—"}</td>
                  <td className="px-4 py-3 text-right font-medium">{(s.amount_xaf || 0).toLocaleString("fr-FR")} XAF</td>
                  <td className="px-4 py-3 text-right">
                    {s.active && (
                      <button
                        type="button" onClick={() => revoke(s)}
                        className="inline-flex items-center gap-1 text-xs text-[#991B1B] hover:underline"
                        data-testid={`subs-revoke-${s.id}`}
                      >
                        <Ban size={12} /> Révoquer
                      </button>
                    )}
                    {!s.active && <span className="font-mono text-[10px] text-foreground/40 uppercase">révoqué</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <ManualSubModal
          onClose={() => setCreating(false)}
          onCreated={(result) => {
            setCreating(false);
            setCreatedToken(result);
            load();
          }}
        />
      )}

      {createdToken && (
        <TokenReveal
          result={createdToken}
          onClose={() => setCreatedToken(null)}
        />
      )}
    </div>
  );
}

function StatBox({ icon: Icon, label, value }) {
  return (
    <div className="bg-white border border-primary/15 rounded-sm p-5">
      {Icon && (
        <div className="w-9 h-9 bg-primary/10 text-primary rounded-sm flex items-center justify-center mb-3">
          <Icon size={16} strokeWidth={1.8} />
        </div>
      )}
      <div className="font-display text-2xl font-semibold text-primary leading-none">{value}</div>
      <div className="font-mono text-[10px] uppercase tracking-widest text-foreground/60 mt-2">{label}</div>
    </div>
  );
}

function ManualSubModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ email: "", plan: "standard", billing: "monthly", duration_days: "", note: "" });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setSubmitting(true);
    try {
      const body = {
        email: form.email.trim().toLowerCase(),
        plan: form.plan,
        billing: form.billing,
        note: form.note || null,
      };
      if (form.duration_days) body.duration_days = parseInt(form.duration_days, 10);
      const { data } = await adminApi.createManualSub(body);
      onCreated(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Échec");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-[#0D1B2A]/70 backdrop-blur-sm"
         onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="relative w-full max-w-md bg-white border border-primary/20 rounded-sm shadow-2xl p-6 md:p-8">
        <button type="button" onClick={onClose} className="absolute top-3 right-3 text-foreground/50 hover:text-foreground"><X size={16} /></button>
        <h2 className="font-display text-2xl font-semibold text-primary mb-2">Activation manuelle</h2>
        <p className="text-xs text-foreground/60 mb-4">Génère un jeton d'accès à communiquer au client (paiement hors PawaPay).</p>
        {err && <div className="mb-3 text-xs text-[#991B1B] bg-[#FEE2E2] border border-[#FECACA] px-3 py-2 rounded-sm">{err}</div>}
        <form onSubmit={submit} className="space-y-3">
          <Row label="Email du client" required>
            <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                   className="w-full bg-transparent text-sm py-2 outline-none"
                   data-testid="subs-manual-email" />
          </Row>
          <div className="grid grid-cols-2 gap-3">
            <Row label="Plan" required>
              <select value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })} className="w-full bg-transparent text-sm py-2 outline-none" data-testid="subs-manual-plan">
                <option value="standard">Standard</option>
                <option value="pro">Pro</option>
              </select>
            </Row>
            <Row label="Facturation" required>
              <select value={form.billing} onChange={(e) => setForm({ ...form, billing: e.target.value })} className="w-full bg-transparent text-sm py-2 outline-none" data-testid="subs-manual-billing">
                <option value="monthly">Mensuel</option>
                <option value="annual">Annuel</option>
              </select>
            </Row>
          </div>
          <Row label="Durée personnalisée (jours) — laisser vide pour la durée par défaut">
            <input type="number" min={1} max={3650} value={form.duration_days}
                   onChange={(e) => setForm({ ...form, duration_days: e.target.value })}
                   className="w-full bg-transparent text-sm py-2 outline-none"
                   data-testid="subs-manual-duration" />
          </Row>
          <Row label="Note (référence de paiement, raison…)">
            <input type="text" value={form.note}
                   onChange={(e) => setForm({ ...form, note: e.target.value })}
                   className="w-full bg-transparent text-sm py-2 outline-none"
                   data-testid="subs-manual-note" />
          </Row>
          <div className="flex items-center justify-end gap-2 pt-3">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-foreground/70">Annuler</button>
            <button type="submit" disabled={submitting}
                    className="inline-flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-sm text-sm font-medium hover:bg-primary-light transition-colors disabled:opacity-60"
                    data-testid="subs-manual-submit">
              {submitting ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />} Activer
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function TokenReveal({ result, onClose }) {
  const token = result?.access_token || "";
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-[#0D1B2A]/85 backdrop-blur-sm"
         data-testid="token-reveal">
      <div className="relative w-full max-w-lg bg-white border-2 border-accent rounded-sm shadow-2xl p-6 md:p-8">
        <h2 className="font-display text-2xl font-semibold text-primary mb-2">Jeton d'accès généré</h2>
        <p className="text-sm text-foreground/70 mb-4">
          Transmettez ce jeton à <strong>{result?.subscription?.email}</strong>. Pour l'activer sur son appareil,
          il devra le coller dans l'URL de chat ou nous contacter. <strong>Ce jeton ne sera plus affiché.</strong>
        </p>
        <div className="bg-[#F8F9FA] border border-primary/15 rounded-sm p-3 font-mono text-xs break-all">
          {token}
        </div>
        <div className="mt-4 flex items-center justify-end gap-2">
          <button type="button" onClick={copy}
                  className="inline-flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-sm text-sm font-medium hover:bg-primary-light transition-colors"
                  data-testid="token-copy-btn">
            <Copy size={13} /> {copied ? "Copié !" : "Copier"}
          </button>
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-foreground/70">Fermer</button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, required, children }) {
  return (
    <label className="block">
      <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-foreground/60 mb-1">
        {label}{required && <span className="text-[#E76F51]"> *</span>}
      </span>
      <div className="border border-primary/20 rounded-sm px-3 focus-within:border-primary transition-colors bg-white">{children}</div>
    </label>
  );
}
